import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Modal, Button, ButtonGroup, Form } from 'react-bootstrap';
import { Canvas as FabricCanvas, Rect, Circle, Line, PencilBrush } from 'fabric';

const COLORS = ['#000000', '#FF0000', '#0066FF', '#00AA00', '#FF9900', '#9933CC'];

const TOOLS = {
  PEN:       'pen',
  LINE:      'line',
  RECT:      'rect',
  CIRCLE:    'circle',
  ERASER:    'eraser',
  SELECT:    'select',
};

const MAX_UNDO = 50;

export default function DrawingCanvas({ show, onHide, onSave, canvasWidth = 800, canvasHeight = 600 }) {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const undoStack = useRef([]);
  const drawStart = useRef(null);
  const tempShape = useRef(null);

  const [tool, setTool] = useState(TOOLS.PEN);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [canUndo, setCanUndo] = useState(false);

  // ── Initialise / destroy fabric canvas when modal opens / closes ──

  useEffect(() => {
    if (!show) return;

    const el = canvasRef.current;
    if (!el) return;

    const fc = new FabricCanvas(el, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: '#FFFFFF',
      isDrawingMode: true,
    });

    fc.freeDrawingBrush = new PencilBrush(fc);
    fc.freeDrawingBrush.color = strokeColor;
    fc.freeDrawingBrush.width = strokeWidth;

    fabricRef.current = fc;
    undoStack.current = [];
    setCanUndo(false);
    setTool(TOOLS.PEN);

    // Snapshot after every object add
    const pushUndo = () => {
      const json = fc.toJSON();
      undoStack.current = [...undoStack.current.slice(-(MAX_UNDO - 1)), json];
      setCanUndo(undoStack.current.length > 1);
    };

    fc.on('object:added', pushUndo);
    fc.on('object:modified', pushUndo);

    // Take initial blank snapshot
    undoStack.current.push(fc.toJSON());

    return () => {
      fc.off('object:added', pushUndo);
      fc.off('object:modified', pushUndo);
      fc.dispose();
      fabricRef.current = null;
    };
  }, [show, canvasWidth, canvasHeight]);

  // ── Sync brush settings when tool / colour / width change ──

  useEffect(() => {
    const fc = fabricRef.current;
    if (!fc) return;

    const isFreeDraw = tool === TOOLS.PEN || tool === TOOLS.ERASER;
    fc.isDrawingMode = isFreeDraw;
    fc.selection = tool === TOOLS.SELECT;

    if (isFreeDraw) {
      if (!fc.freeDrawingBrush) {
        fc.freeDrawingBrush = new PencilBrush(fc);
      }
      fc.freeDrawingBrush.color = tool === TOOLS.ERASER ? '#FFFFFF' : strokeColor;
      fc.freeDrawingBrush.width = tool === TOOLS.ERASER ? strokeWidth * 4 : strokeWidth;
    }
  }, [tool, strokeColor, strokeWidth]);

  // ── Shape drawing (line / rect / circle) via mouse events ──

  useEffect(() => {
    const fc = fabricRef.current;
    if (!fc) return;
    if (![TOOLS.LINE, TOOLS.RECT, TOOLS.CIRCLE].includes(tool)) return;

    const onDown = (opt) => {
      fc.selection = false;
      const pointer = fc.getScenePoint(opt.e);
      drawStart.current = { x: pointer.x, y: pointer.y };

      let shape;
      const base = { stroke: strokeColor, strokeWidth, fill: 'transparent', selectable: false, evented: false };

      if (tool === TOOLS.LINE) {
        shape = new Line([pointer.x, pointer.y, pointer.x, pointer.y], base);
      } else if (tool === TOOLS.RECT) {
        shape = new Rect({ left: pointer.x, top: pointer.y, width: 0, height: 0, ...base });
      } else {
        shape = new Circle({ left: pointer.x, top: pointer.y, radius: 0, ...base });
      }

      fc.add(shape);
      tempShape.current = shape;
    };

    const onMove = (opt) => {
      if (!drawStart.current || !tempShape.current) return;
      const pointer = fc.getScenePoint(opt.e);
      const dx = pointer.x - drawStart.current.x;
      const dy = pointer.y - drawStart.current.y;
      const shape = tempShape.current;

      if (tool === TOOLS.LINE) {
        shape.set({ x2: pointer.x, y2: pointer.y });
      } else if (tool === TOOLS.RECT) {
        shape.set({
          left: dx >= 0 ? drawStart.current.x : pointer.x,
          top:  dy >= 0 ? drawStart.current.y : pointer.y,
          width:  Math.abs(dx),
          height: Math.abs(dy),
        });
      } else {
        const r = Math.sqrt(dx * dx + dy * dy) / 2;
        shape.set({
          left: Math.min(drawStart.current.x, pointer.x),
          top:  Math.min(drawStart.current.y, pointer.y),
          radius: r,
        });
      }
      fc.requestRenderAll();
    };

    const onUp = () => {
      if (tempShape.current) {
        tempShape.current.set({ selectable: true, evented: true });
      }
      drawStart.current = null;
      tempShape.current = null;
    };

    fc.on('mouse:down', onDown);
    fc.on('mouse:move', onMove);
    fc.on('mouse:up', onUp);

    return () => {
      fc.off('mouse:down', onDown);
      fc.off('mouse:move', onMove);
      fc.off('mouse:up', onUp);
    };
  }, [tool, strokeColor, strokeWidth]);

  // ── Actions ──

  const handleUndo = useCallback(() => {
    const fc = fabricRef.current;
    if (!fc || undoStack.current.length <= 1) return;

    undoStack.current.pop(); // discard current
    const prev = undoStack.current[undoStack.current.length - 1];
    fc.loadFromJSON(prev).then(() => {
      fc.requestRenderAll();
      setCanUndo(undoStack.current.length > 1);
    });
  }, []);

  const handleClear = useCallback(() => {
    const fc = fabricRef.current;
    if (!fc) return;
    fc.clear();
    fc.backgroundColor = '#FFFFFF';
    fc.requestRenderAll();
    undoStack.current = [fc.toJSON()];
    setCanUndo(false);
  }, []);

  const exportAs = useCallback(async (format) => {
    const fc = fabricRef.current;
    if (!fc) return;

    // Deselect all so selection handles don't appear in export
    fc.discardActiveObject();
    fc.requestRenderAll();

    let file;
    const ts = Date.now();

    if (format === 'svg') {
      const svgString = fc.toSVG();
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      file = new File([blob], `drawing_${ts}.svg`, { type: 'image/svg+xml' });
    } else {
      const dataUrl = fc.toDataURL({ format: 'png', multiplier: 2 });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      file = new File([blob], `drawing_${ts}.png`, { type: 'image/png' });
    }

    onSave(file);
    onHide();
  }, [onSave, onHide]);

  // ── Render ──

  const toolBtn = (id, label) => (
    <Button
      key={id}
      size="sm"
      variant={tool === id ? 'primary' : 'outline-secondary'}
      onClick={() => setTool(id)}
      style={{ minWidth: 60 }}
    >
      {label}
    </Button>
  );

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Drawing Canvas</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-3">
        {/* Toolbar */}
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <ButtonGroup>
            {toolBtn(TOOLS.SELECT, 'Select')}
            {toolBtn(TOOLS.PEN, 'Pen')}
            {toolBtn(TOOLS.LINE, 'Line')}
            {toolBtn(TOOLS.RECT, 'Rect')}
            {toolBtn(TOOLS.CIRCLE, 'Circle')}
            {toolBtn(TOOLS.ERASER, 'Eraser')}
          </ButtonGroup>

          <div className="d-flex align-items-center gap-1">
            {COLORS.map(c => (
              <button
                key={c}
                onClick={() => setStrokeColor(c)}
                style={{
                  width: 24, height: 24, borderRadius: '50%',
                  backgroundColor: c, border: strokeColor === c ? '3px solid #333' : '2px solid #ccc',
                  cursor: 'pointer', padding: 0,
                }}
                aria-label={`Color ${c}`}
              />
            ))}
            <Form.Control
              type="color"
              value={strokeColor}
              onChange={e => setStrokeColor(e.target.value)}
              style={{ width: 28, height: 28, padding: 0, border: 'none', cursor: 'pointer' }}
              title="Custom color"
            />
          </div>

          <div className="d-flex align-items-center gap-2" style={{ minWidth: 160 }}>
            <span className="small text-muted">Width</span>
            <Form.Range
              min={1} max={20} value={strokeWidth}
              onChange={e => setStrokeWidth(Number(e.target.value))}
              style={{ width: 100 }}
            />
            <span className="small" style={{ minWidth: 20 }}>{strokeWidth}</span>
          </div>

          <ButtonGroup>
            <Button size="sm" variant="outline-secondary" disabled={!canUndo} onClick={handleUndo}>
              Undo
            </Button>
            <Button size="sm" variant="outline-danger" onClick={handleClear}>
              Clear
            </Button>
          </ButtonGroup>
        </div>

        {/* Canvas */}
        <div style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'auto', maxHeight: '70vh' }}>
          <canvas ref={canvasRef} />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="outline-primary" onClick={() => exportAs('svg')}>Save as SVG</Button>
        <Button variant="primary" onClick={() => exportAs('png')}>Save as PNG</Button>
      </Modal.Footer>
    </Modal>
  );
}
